import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Search, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DateInputDDMMYYYY from '../../components/DateInputDDMMYYYY/DateInputDDMMYYYY';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SearchCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const SearchTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SearchInputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SearchField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.9375rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

`;

const ResultsCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ResultsTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 1.5rem 0;
`;

const OrderCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  transition: all 0.2s;
  
  &:hover {
    border-color: #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
  }
`;

const OrderTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const OrderInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 0.5rem;
  
  span:first-child {
    color: #64748b;
    font-weight: 500;
  }
  
  span:last-child {
    color: #1e293b;
  }
`;

const CompleteButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #059669;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  justify-content: center;
  
  &:hover {
    background: #047857;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  color: #64748b;
  font-size: 0.9375rem;
`;

const SearchPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchName, setSearchName] = useState('');
  const [searchBirthday, setSearchBirthday] = useState('');
  const [incompleteOrders, setIncompleteOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);

  useEffect(() => {
    fetchIncompleteOrders();
  }, []);

  const fetchIncompleteOrders = async () => {
    const userData = localStorage.getItem('user');
    if (!userData) return;

    try {
      const user = JSON.parse(userData);
      const userId = user.id || user._id;
      if (!userId) return;

      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/orders/incomplete/${userId}?includeWithReason=true`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.incompleteOrders) {
        setIncompleteOrders(response.data.incompleteOrders);
        setFilteredOrders(response.data.incompleteOrders);
      }
    } catch (error) {
      console.error('Error fetching incomplete orders:', error);
    }
  };

  useEffect(() => {
    if (!searchName && !searchBirthday) {
      setFilteredOrders(incompleteOrders);
      return;
    }

    const filtered = incompleteOrders.filter(order => {
      let nameMatch = false;
      let dateMatch = false;

      if (searchName && searchName.trim()) {
        const searchTerm = searchName.toLowerCase().trim();
        const patientName = (order.prescription?.patientName || '').toLowerCase();
        const phoneNumber = (order.prescription?.phoneNumber || '').replace(/\D/g, '');
        const searchPhone = searchTerm.replace(/\D/g, '');
        
        nameMatch = patientName.includes(searchTerm) || 
                   (searchPhone && phoneNumber.includes(searchPhone));
      }

      if (searchBirthday && searchBirthday.trim()) {
        const searchTerm = searchBirthday.trim();
        const checkDate = (date) => {
          if (!date) return false;
          const dateStr = String(date);
          if (dateStr.includes(searchTerm)) return true;

          try {
            const d = date instanceof Date ? date : new Date(date);
            if (isNaN(d.getTime())) return false;

            if (/^\d{4}-\d{2}-\d{2}$/.test(searchTerm)) {
              const y = d.getFullYear();
              const m = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              if (`${y}-${m}-${day}` === searchTerm) return true;
            }
            
            const year = d.getFullYear().toString();
            const month = String(d.getMonth() + 1);
            const monthPadded = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate());
            const dayPadded = String(d.getDate()).padStart(2, '0');
            
            if (searchTerm.length === 4 && /^\d{4}$/.test(searchTerm)) {
              if (year === searchTerm) return true;
            }
            
            if (year.includes(searchTerm)) return true;
            
            const formats = [
              `${month}/${day}/${year}`,
              `${monthPadded}/${dayPadded}/${year}`,
              `${year}-${monthPadded}-${dayPadded}`,
            ];
            
            return formats.some(fmt => fmt.includes(searchTerm));
          } catch (e) {
            return false;
          }
        };
        
        const patientBirthday = order.prescription?.patientBirthday;
        const prescriptionDate = order.prescription?.prescriptionDate;
        const visitDate = order.visitor?.visitDate;
        
        if (patientBirthday) {
          dateMatch = checkDate(patientBirthday);
        }
        if (!dateMatch && prescriptionDate) {
          dateMatch = checkDate(prescriptionDate);
        }
        if (!dateMatch && visitDate) {
          dateMatch = checkDate(visitDate);
        }
      }

      if (searchName && searchName.trim() && searchBirthday && searchBirthday.trim()) {
        return nameMatch || dateMatch;
      } else if (searchName && searchName.trim()) {
        return nameMatch;
      } else if (searchBirthday && searchBirthday.trim()) {
        return dateMatch;
      }
      
      return false;
    });
    
    setFilteredOrders(filtered);
  }, [searchName, searchBirthday, incompleteOrders]);

  const handleCompleteOrder = (incompleteOrder) => {
    try {
      const visitor = incompleteOrder.visitor;
      const prescription = incompleteOrder.prescription;
      
      const visitorData = {
        gender: visitor?.gender || 'male',
        ageGroup: visitor?.ageGroup || 'adult',
        visitDate: visitor?.visitDate || new Date().toISOString()
      };
      
      sessionStorage.setItem('visitorData', JSON.stringify(visitorData));
      
      if (visitor?._id) {
        sessionStorage.setItem('visitorId', visitor._id);
      }
      
      if (prescription) {
        sessionStorage.setItem('existingPrescription', JSON.stringify(prescription));
        if (prescription._id) {
          sessionStorage.setItem('prescriptionId', prescription._id);
        }
      }
      
      navigate('/prescription');
    } catch (error) {
      console.error('Error completing order:', error);
      alert('Error loading order data. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  return (
    <PageContainer>
      <SearchCard>
        <SearchTitle>
          <Search size={24} />
          {t('searchPage.title')}
        </SearchTitle>
        <SearchInputGroup>
          <SearchField>
            <Label htmlFor="search-name">{t('searchPage.searchByName')}</Label>
            <Input
              id="search-name"
              type="text"
              placeholder={t('searchPage.namePlaceholder')}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </SearchField>
          <SearchField>
            <Label htmlFor="search-birthday">{t('searchPage.searchByDate')}</Label>
            <DateInputDDMMYYYY
              id="search-birthday"
              value={searchBirthday}
              onChange={setSearchBirthday}
              placeholder="dd/mm/yyyy"
              aria-label="Search by birthday or visit date"
            />
          </SearchField>
        </SearchInputGroup>
      </SearchCard>

      {(searchName.trim() || searchBirthday.trim()) && (
        <ResultsCard>
          <ResultsTitle>
            {t('searchPage.results', { count: filteredOrders.length })}
          </ResultsTitle>
          {filteredOrders.length > 0 ? (
            filteredOrders.map((order, index) => (
              <OrderCard key={index}>
                <OrderTitle>
                  {order.prescription?.patientName || 'Visitor Entry Only'}
                </OrderTitle>
                <OrderInfo>
                  {order.prescription?.patientName && (
                    <InfoItem>
                      <span>Patient Name:</span>
                      <span>{order.prescription.patientName}</span>
                    </InfoItem>
                  )}
                  {order.prescription?.patientAge && (
                    <InfoItem>
                      <span>Age:</span>
                      <span>{order.prescription.patientAge} years</span>
                    </InfoItem>
                  )}
                  {(order.prescription?.patientBirthday || order.prescription?.prescriptionDate) && (
                    <InfoItem>
                      <span>Birthday:</span>
                      <span>
                        {order.prescription?.patientBirthday 
                          ? formatDate(order.prescription.patientBirthday)
                          : formatDate(order.prescription?.prescriptionDate)}
                      </span>
                    </InfoItem>
                  )}
                  {order.prescription?.phoneNumber && (
                    <InfoItem>
                      <span>Phone:</span>
                      <span>{order.prescription.phoneNumber}</span>
                    </InfoItem>
                  )}
                  {order.visitor?.visitDate && (
                    <InfoItem>
                      <span>Visit Date:</span>
                      <span>{formatDate(order.visitor.visitDate)}</span>
                    </InfoItem>
                  )}
                  {order.reason && (
                    <InfoItem>
                      <span>Reason:</span>
                      <span>{order.reason}</span>
                    </InfoItem>
                  )}
                  {order.salesPerson && (
                    <InfoItem>
                      <span>Employee:</span>
                      <span>
                        {order.salesPerson.firstName || ''} {order.salesPerson.lastName || ''}
                        {order.salesPerson.employeeId && ` (${order.salesPerson.employeeId})`}
                      </span>
                    </InfoItem>
                  )}
                  {order.store && (
                    <InfoItem>
                      <span>Store:</span>
                      <span>{order.store.name || order.store.storeCode || 'N/A'}</span>
                    </InfoItem>
                  )}
                </OrderInfo>
                <CompleteButton onClick={() => handleCompleteOrder(order)}>
                  <CheckCircle size={18} />
                  Complete Order
                </CompleteButton>
              </OrderCard>
            ))
          ) : (
            <NoResults>
              {t('searchPage.noResults')}
            </NoResults>
          )}
        </ResultsCard>
      )}
    </PageContainer>
  );
};

export default SearchPage;

